import React, { useMemo } from 'react';
import styled from 'styled-components';

import MaterialIcon from '@/components/atom/MaterialIcon';
import OpenInNew from '@/components/atom/icon/OpenInNew';
import { EllipsisTextStyle } from '@/components/atom/text/DefaultText';
import { Tablet } from '@/styles/layout';
import theme from '@/styles/theme';
import { MagazineLineItemVo } from '@/types/magazine';
import { isWebView } from '@/utils/mobile/RNPlugins';

interface Props {
  magazine: MagazineLineItemVo;
  isExternal?: boolean;
  isLast?: boolean;
  titleAsH2: boolean;
}
const ListMagazineLineItem = ({ magazine, isExternal, isLast, titleAsH2 }: Props) => {
  const { image, date } = magazine;

  const title = useMemo(
    () => (isWebView() ? magazine.mobileTitle : magazine.title),
    [magazine.mobileTitle, magazine.title]
  );

  const description = useMemo(
    () => (isWebView() ? magazine.mobileDescription : magazine.description),
    [magazine.description, magazine.mobileDescription]
  );

  const siteName = useMemo(() => {
    if (isExternal && magazine.siteName) {
      return magazine.siteName;
    }

    return '';
  }, [isExternal, magazine.siteName]);

  return (
    <Wrapper isLast={isLast}>
      <Content>
        <Title as={titleAsH2 ? 'h2' : 'h3'}>{title}</Title>
        <Description as={titleAsH2 ? 'h3' : 'p'}>{description}</Description>
        {isExternal ? (
          <OuterLink>
            {siteName} <OpenInNew />
          </OuterLink>
        ) : (
          <Date>{date}</Date>
        )}
      </Content>
      {image === '' ? (
        <MaterialIcon name={'chevron_right'} color={theme.color.gray400} />
      ) : (
        <ImageWrapper>
          <Image src={image} alt='이미지' />
        </ImageWrapper>
      )}
    </Wrapper>
  );
};

export default ListMagazineLineItem;

const Wrapper = styled.div<{ isLast?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
  ${({ theme, isLast }) => `
    border-bottom: ${!isLast ? `1px solid ${theme.color.gray30}` : 'none'};
  `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h2`
  color: ${theme.color.gray800};
  ${theme.typography.UI17SB};
  margin-bottom: 2px;
  ${EllipsisTextStyle};
  word-break: unset;
`;

const Description = styled.h3`
  color: ${theme.color.gray500};
  ${theme.typography.UI13R};
  ${EllipsisTextStyle};
  margin-bottom: 8px;
  word-break: unset;
`;

const Date = styled.time`
  color: ${theme.color.gray300};
  ${theme.typography.UI13R};
`;

const OuterLink = styled.div`
  color: ${theme.color.gray300};
  ${theme.typography.UI13R};
  display: flex;
  align-items: center;
  & > svg {
    margin-left: 2px;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  min-width: 96px;
  max-width: 128px;
  height: 100%;
  border-radius: 12px;
  margin-left: 24px;

  @media screen and (max-width: ${Tablet}) {
    max-width: 96px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  min-height: 72px;
  max-height: 96px;
  border-radius: 12px;
  border: 1px solid ${theme.color.gray40};
  object-fit: cover;
`;
